import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Settings } from "lucide-react";
import {
  updateStrategy,
  type BuildingTarget,
  type StrategyField,
  type StrategyStepBound,
} from "@/lib/api/building";
import { SectionPanel } from "@/components/ui/section-panel";
import { cn } from "@/lib/utils";
import { cardSurface, inputGlowWrap, inputGlowInner } from "@/lib/card-styles";
import {
  STRATEGY_FIELDS,
  STRATEGY_FIELD_LABELS,
  STRATEGY_STEP_BOUND_FIELD_LABELS,
} from "./strategy-fields";

type StepBoundKey = "temp_min" | "temp_max" | "co2_limit_ppm";
const STEP_BOUND_KEYS: StepBoundKey[] = [
  "temp_min",
  "temp_max",
  "co2_limit_ppm",
];
type StepBoundFormEntry = Partial<Record<StepBoundKey, string>>;

// BuildingTarget step_bounds keys — a frozen bin of the 10-step occupancy score, not the
// on-screen indicator (docs/spec/knowledge-base.md § Zone KPI scale). Busy is the live-field
// default edited above; the other two keys get their own rows here.
const STRATEGY_OCCUPANCY_KEYS = [
  { id: "unoccupied", name_fi: "Tyhjä" },
  { id: "partial", name_fi: "Osittain käytössä" },
  { id: "busy", name_fi: "Täynnä" },
] as const;
const BUSY_STEP_ID = "busy";
const nonDefaultSteps = STRATEGY_OCCUPANCY_KEYS.filter(
  (s) => s.id !== BUSY_STEP_ID,
);

// Strategian rajat -editori (BuildingTarget bounds). Strategy tier. Editing is operator-gated.
export function OptimizationStrategySection({
  buildingId,
  strategy,
  operator,
  onRefresh,
}: {
  buildingId: string;
  strategy: BuildingTarget | null;
  operator: boolean;
  onRefresh: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<Partial<Record<StrategyField, string>>>({});
  const [stepBoundsForm, setStepBoundsForm] = useState<
    Record<string, StepBoundFormEntry>
  >({});

  useEffect(() => {
    if (!strategy) return;
    setForm(
      Object.fromEntries(
        STRATEGY_FIELDS.map((f) => [
          f,
          strategy[f] != null ? String(strategy[f]) : "",
        ]),
      ),
    );
    setStepBoundsForm(
      Object.fromEntries(
        nonDefaultSteps.map((step) => {
          const bound = strategy.step_bounds[step.id];
          return [
            step.id,
            {
              temp_min: bound?.temp_min != null ? String(bound.temp_min) : "",
              temp_max: bound?.temp_max != null ? String(bound.temp_max) : "",
              co2_limit_ppm:
                bound?.co2_limit_ppm != null ? String(bound.co2_limit_ppm) : "",
            },
          ];
        }),
      ),
    );
  }, [strategy]);

  async function handleSave() {
    setSaving(true);
    try {
      const payload = Object.fromEntries(
        STRATEGY_FIELDS.map((f) => [f, form[f] ? parseFloat(form[f]!) : null]),
      ) as Record<StrategyField, number | null>;

      // Full-replace PUT (services/building/.../routes/buildings.py `update_strategy`) — start
      // from every step the strategy already carries so a step this session's KB fetch didn't
      // surface (e.g. the KB route not yet serving `kpi_steps`) is never silently dropped, then
      // overlay only the steps actually edited here.
      const stepBounds: Record<string, StrategyStepBound> = {
        ...(strategy?.step_bounds ?? {}),
      };
      for (const step of nonDefaultSteps) {
        const entry = stepBoundsForm[step.id];
        stepBounds[step.id] = {
          temp_min: entry?.temp_min ? parseFloat(entry.temp_min) : null,
          temp_max: entry?.temp_max ? parseFloat(entry.temp_max) : null,
          co2_limit_ppm: entry?.co2_limit_ppm
            ? parseFloat(entry.co2_limit_ppm)
            : null,
        };
      }

      await updateStrategy(buildingId, { ...payload, step_bounds: stepBounds });
      setEditing(false);
      onRefresh();
      toast.success("Strategia tallennettu");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Tallennus epäonnistui");
    } finally {
      setSaving(false);
    }
  }

  return (
    <SectionPanel icon={Settings} title="Optimointistrategia">
      {editing && operator ? (
        <div className="space-y-3">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Muokkaa strategiaa
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {STRATEGY_FIELDS.map((field) => {
              const meta = STRATEGY_FIELD_LABELS[field];
              return (
                <div key={field}>
                  <label className="text-[11px] text-muted-foreground block mb-1">
                    {meta.label} ({meta.unit})
                  </label>
                  <div className={inputGlowWrap}>
                    <div className={inputGlowInner}>
                      <input
                        type="number"
                        value={form[field] ?? ""}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, [field]: e.target.value }))
                        }
                        placeholder="—"
                        className="w-full bg-transparent px-2.5 py-1.5 text-sm placeholder:text-muted-foreground focus:outline-none"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {nonDefaultSteps.length > 0 && (
            <div className="space-y-2">
              <p className="text-[11px] text-muted-foreground">
                Käyttöastekohtaiset rajat
              </p>
              {nonDefaultSteps.map((step) => (
                <div
                  key={step.id}
                  className={cn(cardSurface, "p-2.5 space-y-2")}
                >
                  <p className="text-xs font-medium">{step.name_fi}</p>
                  <div className="grid grid-cols-3 gap-2">
                    {STEP_BOUND_KEYS.map((key) => {
                      const meta = STRATEGY_STEP_BOUND_FIELD_LABELS[key];
                      return (
                        <div key={key}>
                          <label className="text-[10px] text-muted-foreground block mb-1">
                            {meta.label} ({meta.unit})
                          </label>
                          <div className={inputGlowWrap}>
                            <div className={inputGlowInner}>
                              <input
                                type="number"
                                value={stepBoundsForm[step.id]?.[key] ?? ""}
                                onChange={(e) =>
                                  setStepBoundsForm((s) => ({
                                    ...s,
                                    [step.id]: {
                                      ...s[step.id],
                                      [key]: e.target.value,
                                    },
                                  }))
                                }
                                placeholder="—"
                                className="w-full bg-transparent px-2.5 py-1.5 text-sm placeholder:text-muted-foreground focus:outline-none"
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary text-primary-foreground px-3 py-1.5 text-xs font-medium hover:opacity-90 disabled:opacity-50 transition"
            >
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Tallenna
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="rounded-lg border border-border px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground transition"
            >
              Peruuta
            </button>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Nykyiset rajat
            </p>
            {operator && (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="text-xs text-primary hover:underline"
              >
                Muokkaa
              </button>
            )}
          </div>
          {strategy ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                {STRATEGY_FIELDS.map((field) => {
                  const meta = STRATEGY_FIELD_LABELS[field];
                  const val = strategy[field];
                  return (
                    <div key={field} className={cn(cardSurface, "p-2.5")}>
                      <p className="text-muted-foreground text-[10px] mb-0.5">
                        {meta.label}
                      </p>
                      <p className="font-semibold text-foreground">
                        {val != null ? `${val} ${meta.unit}` : "—"}
                      </p>
                    </div>
                  );
                })}
              </div>
              {nonDefaultSteps.length > 0 && (
                <div className="space-y-2">
                  <p className="text-[10px] text-muted-foreground">
                    Käyttöastekohtaiset rajat
                  </p>
                  {nonDefaultSteps.map((step) => {
                    const bound = strategy.step_bounds[step.id];
                    return (
                      <div key={step.id} className={cn(cardSurface, "p-2.5")}>
                        <p className="text-muted-foreground text-[10px] mb-1">
                          {step.name_fi}
                        </p>
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          {STEP_BOUND_KEYS.map((key) => {
                            const meta = STRATEGY_STEP_BOUND_FIELD_LABELS[key];
                            const val = bound?.[key];
                            return (
                              <div key={key}>
                                <p className="text-muted-foreground text-[10px] mb-0.5">
                                  {meta.label}
                                </p>
                                <p className="font-semibold text-foreground">
                                  {val != null ? `${val} ${meta.unit}` : "—"}
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Ei strategiaa asetettu.
            </p>
          )}
        </div>
      )}
    </SectionPanel>
  );
}
